import { useRef, useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  List,
  ListItemText,
  ListItemButton, // Import ListItemButton
  Popover,
  Typography
} from '@mui/material';

import { alpha, styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import { logoutUser } from 'src/utils/authService';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(({ theme }) => ({
  background: alpha(theme.palette.grey[900], 0.05), // Using grey[900] as black-like color
  padding: theme.spacing(2)
}));

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

// User box component to get user details
function HeaderUserbox() {
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const dispatch = useDispatch();

  const isAdmin = userDetails.UserRole === 'Admin';
  const isFinanceManager = userDetails.isFinanceManager;
  const isSubscribed = userDetails?.TownshipPermits?.length !== 0;

  const location = useLocation();
  const [activePath, setActivePath] = useState(
    decodeURIComponent(location.pathname)
  );

  useEffect(() => {
    setActivePath(decodeURIComponent(location.pathname));
  }, [location.pathname]);

  // Default avatar path when no user image is available
  const defaultAvatar = '/static/images/avatars/default-avatar.png';
  
  // Get user avatar from userData if available, otherwise use default
  const userAvatar = userData?.ProfilePicture || userData?.ImageURL || userData?.ProfileImage || userData?.Avatar || defaultAvatar;

  const [avatarSrc, setAvatarSrc] = useState(userAvatar);

  // Update avatar when userData changes
  useEffect(() => {
    const newAvatar = userData?.ProfilePicture || userData?.ImageURL || userData?.ProfileImage || userData?.Avatar || defaultAvatar;
    setAvatarSrc(newAvatar);
  }, [userData, defaultAvatar]);

  const user = {
    name: `${userData.FirstName} ${userData.LastName}`,
    avatar: avatarSrc,
    userRole: userData.UserRole === 'User' ? 'User' : userData.UserRole
  };

  const navigate = useNavigate();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSignOut = () => {
    logoutUser(dispatch, navigate);
  };

  const handleButtonClick = (path) => {
    setActivePath(path);
    setOpen(false);
  };

  const handleAvatarError = () => {
    // Fallback to default avatar if image fails to load
    if (avatarSrc !== defaultAvatar) {
      setAvatarSrc(defaultAvatar);
    }
  };

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar 
          variant="rounded" 
          alt={user.name} 
          src={avatarSrc}
          onError={handleAvatarError}
        />
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {user.userRole}
            </UserBoxDescription>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar 
            variant="rounded" 
            alt={user.name} 
            src={avatarSrc}
            onError={handleAvatarError}
          />
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {user.userRole}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItemButton
            component={NavLink}
            to={`/user-profile/${userDetails?.TownshipID}`}
            onClick={() =>
              handleButtonClick(`/user-profile/${userDetails?.TownshipID}`)
            }
            selected={activePath === `/user-profile/${userDetails?.TownshipID}`}
          >
            <AccountBoxTwoToneIcon fontSize="small" sx={{ mr: 1 }} />
            <ListItemText primary="My Profile" />
          </ListItemButton>

          {(isSubscribed && (isAdmin || isFinanceManager)) && (
            <ListItemButton
              component={NavLink}
              to={`/township-configuration/${userDetails?.TownshipID}`}
              onClick={() =>
                handleButtonClick(
                  `/township-configuration/${userDetails?.TownshipID}`
                )
              }
              selected={
                activePath ===
                `/township-configuration/${userDetails?.TownshipID}`
              }
            >
              <AccountTreeTwoToneIcon fontSize="small" sx={{ mr: 1 }} />
              <ListItemText
                primary={
                  isFinanceManager
                    ? 'Finance Configuration'
                    : 'Township Configuration'
                }
              />
            </ListItemButton>
          )}

          <ListItemButton
            component={NavLink}
            to={`/account-settings/${userDetails?.TownshipID}`}
            onClick={() =>
              handleButtonClick(`/account-settings/${userDetails?.TownshipID}`)
            }
            selected={
              activePath === `/account-settings/${userDetails?.TownshipID}`
            }
          >
            <ManageAccountsIcon fontSize="small" sx={{ mr: 1 }} />
            <ListItemText primary="Account Settings" />
          </ListItemButton>
        </List>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button color="primary" fullWidth onClick={handleSignOut}>
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
